import React from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import type { ReferralDecisionTask } from "@modules/shared/reducer/absence-review/types";
import type { ReferralDeclinedReason, ReferralQuestionAnswer } from "@modules/recording/types";
import className from "classnames";
import { Label, Button, Input, DatePicker, Icon, ComboBox, View } from "@components";
import type { OptionType } from "@components";
import actionCreators from "@modules/reporting/actionCreator";
import { isFirstCareUser } from "@modules/account/selectors";

type PropsFromDispatch = $Exact<{
  updateManagerLedReferralDraft: typeof actionCreators.UpdateManagerLedReferralDraft,
  submitManagerLedReferralDraft: typeof actionCreators.SubmitManagerLedReferralDraft,
}>;
type OwnProps = $Exact<{
  task: ReferralDecisionTask,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromDispatch,
}>;

type State = $Exact<{
  task: ReferralDecisionTask,
}>;

export class ManagerReferralTaskForm extends React.Component<Props, State> {
  state = {
    task: null,
    preselectedDeclineReasonId: null, // temporary storage for the reason id in case user change his mind (that will auto select the task in the combo)
  };

  componentDidMount() {
    this.setState({
      task: { ...this.props.task },
      preselectedDeclineReasonId: this?.props?.task?.declineReason?.id || null,
    });
  }

  componentDidUpdate(state: State) {
    if (this.props.task.id !== state.task.id) {
      this.setState({
        task: this.props.task,
      });
    }
  }

  convertSelectData = referrals => {
    return (
      referrals &&
      referrals.map(d => {
        return { value: d, label: d };
      })
    );
  };
  generateQuestionField = (fieldType, questionIndex, isMandatory) => {
    const currentQuestion = this.task.questionAnswers[questionIndex].question;
    const currentAnswer = this.task.questionAnswers[questionIndex].answer || null;
    switch (fieldType) {
      case "CONFIRMATION":
        return <div />;
      case "FREE_TEXT":
        return (
          <Input value={currentAnswer?.value || ""} onChange={this.updateFreeText(questionIndex)} />
        );
      case "DATE_PICK":
        return (
          <DatePicker value={currentAnswer?.value} onChange={this.updateAnswer(questionIndex)} />
        );
      case "MULTI_SELECT":
        return (
          <ComboBox
            onChange={this.updateMultiSelect(questionIndex)}
            options={this.convertSelectData(currentQuestion.selectableValues || [])}
            isMulti={true}
            value={currentAnswer && (currentAnswer?.value || [])}
          />
        );
      case "SINGLE_SELECT":
        return (
          <ComboBox
            onChange={this.updateSingleSelect(questionIndex)}
            options={this.convertSelectData(currentQuestion.selectableValues)}
            value={currentAnswer && currentAnswer?.value}
            withoutEmptyOption={isMandatory}
          />
        );
    }
    return true;
  };
  generateQuestions = (questionAnswers: ReferralQuestionAnswer) => {
    return (
      questionAnswers ||
      this.task.questionAnswers.map((qa: ReferralQuestionAnswer, idx: number) => {
        return (
          <div key={qa.question.id}>
            <Label className="small-font-size">{qa.question.questionText}</Label>
            {qa.question.isMandatory && (
              <Icon icon="asterisk" size="xs" className="text-warning ml-2" />
            )}
            {this.generateQuestionField(qa.question.questionType, idx, qa.question.isMandatory)}
          </div>
        );
      })
    );
  };
  generateDeclinedReasons = () => {
    return (
      <div className={"d-flex flex-column"}>
        <Label for="declineReason" className="small-font-size">
          {this.props.t("REASON_FOR_DECLINE")}
        </Label>
        <ComboBox
          onChange={this.updateDeclinedReason}
          value={this.task.declineReason?.id || this.state.preselectedDeclineReasonId}
          options={this.task.declineReasons.map((dr: ReferralDeclinedReason) => {
            return { label: dr.reason, value: dr.id };
          })}
        />
      </div>
    );
  };

  updateAgreeToReferral = (agreedToReferral: boolean) => () => {
    this.setState({
      ...this.state,
      task: {
        ...this.props.task,
        agreedToReferral,
        questionAnswers: this.props.task.questionAnswers.map(qa => {
          return { question: qa.question, answer: null };
        }),
      },
    });
  };
  updateDeclinedReason = (declineReasonId: number) => {
    this.setState({
      ...this.state,
      task: {
        ...this.state.task,
        declineReason: {
          id: declineReasonId,
          isActive: true,
        },
      },
      preselectedDeclineReasonId: declineReasonId,
    });
  };
  updateAnswer = (idx: number) => (val: string | Array<string | number>) => {
    let state = { ...this.state };
    state.task.questionAnswers[idx].answer = {
      type: "TEXT",
      value: val,
    };
    this.setState({ ...state });
  };
  updateFreeText = a => b => {
    this.updateAnswer(a)(b.currentTarget.value);
  };
  updateSingleSelect = (idx: number) => (val: OptionType) => {
    this.updateAnswer(idx)(val);
  };
  updateMultiSelect = (idx: number) => (val: OptionType) => {
    this.updateAnswer(idx)(val);
  };

  submitForm = () => {
    this.props.submitManagerLedReferralDraft({
      clientId: this.props.clientId,
      task: this.task,
    });
  };

  saveDraft = () => {
    this.props.updateManagerLedReferralDraft({
      clientId: this.props.clientId,
      task: this.task,
    });
  };

  get task(): ReferralDecisionTask {
    return this.state.task || this.props.task || {};
  }

  get isValid() {
    if (this.task.agreedToReferral === true) {
      return (
        this.task.questionAnswers.filter(
          (qa: ReferralQuestionAnswer) =>
            qa.question.isMandatory === true &&
            (qa.answer === null || qa.answer?.value?.length === 0),
        ).length < 1
      );
    }
    return !!this.state.preselectedDeclineReasonId;
  }

  render() {
    const { agreedToReferral } = this.task;
    const isValid = this.isValid;
    return (
      <View gap={5}>
        {this.task.referralSupplier && (
          <p
            className={"small-font-size"}
            dangerouslySetInnerHTML={{
              __html: this.props.t("MANAGER_REFERRAL_QUESTION_LONG_TEXT", {
                SUPPLIER_NAME: this.task.referralSupplier.name,
                SUPPLIER_DESCRIPTION: this.task.referralSupplier.description,
              }),
            }}
          />
        )}
        <View direction="row" className="button-group" justifyContent="space-between">
          <Button
            onClick={this.updateAgreeToReferral(false)}
            className={[agreedToReferral === false && "active", "flex-1"]}
          >
            {this.props.t("NO")}
          </Button>
          <Button
            onClick={this.updateAgreeToReferral(true)}
            className={[agreedToReferral === true && "active", "flex-1"]}
          >
            {this.props.t("YES")}
          </Button>
        </View>

        {agreedToReferral !== null && agreedToReferral !== true && this.generateDeclinedReasons()}
        {agreedToReferral === true && this.generateQuestions()}

        <View gap={2} direction="row" justifyContent="end">
          <Button materialIcon="save_as" disabled={!isValid} onClick={this.saveDraft}>
            {this.props.t("SAVE_DRAFT")}
          </Button>
          <Button materialIcon="replay_circle_filled" disabled={!isValid} onClick={this.submitForm}>
            {this.props.t("SUBMIT")}
          </Button>
        </View>
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    isFirstCareUser: isFirstCareUser(state),
  };
};

const mapDispatchToProps = {
  updateManagerLedReferralDraft: actionCreators.UpdateManagerLedReferralDraft,
  submitManagerLedReferralDraft: actionCreators.SubmitManagerLedReferralDraft,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(ManagerReferralTaskForm));
